/**
 * Movement over a {@link Space}: shortest path ({@link aStar}) and
 * movement-budget reach ({@link reachable}). **Generic over the grid** — they
 * take a `Space<P>` and use only `space.key`/`distance`/`neighbors`, so the
 * identical code runs on hex and square alike.
 * Both are parameterised by a **cost function**, so the geometry stays free of
 * any rules or map representation — terrain, blockers, difficult ground, and
 * occupancy all enter through `enterCost`; returning `Infinity` marks a cell
 * impassable.
 *
 * This is the render-free, rules-free core. The encounter's `move_token` intent
 * supplies an `enterCost` derived from the live map + combatant state; the VTT
 * reuses the same functions to paint reachable cells and preview paths.
 */

import type { Space } from "./space";

/** Cost to *enter* `to`, having come from `from`. `Infinity` ⇒ impassable. */
export type EnterCost<P> = (to: P, from: P) => number;

export type PathOptions<P> = {
  /** Cost to enter a cell (default: 1 per step). Return `Infinity` to block. */
  enterCost?: EnterCost<P>;
  /**
   * Admissible heuristic (default: `space.distance`). Must never overestimate
   * the true remaining cost or A* may return a non-optimal path; the default is
   * safe whenever every `enterCost` is `>= 1`.
   */
  heuristic?: (from: P, goal: P) => number;
  /**
   * A **cost-free** preference among EQUAL-cost paths: a per-step penalty to
   * *minimize* as a secondary key. It rides only a tiny epsilon of the search
   * priority and the equal-cost relaxation tiebreak — never `enterCost`/`g` — so
   * it can't change which cells are reachable, a path's true length, or its
   * optimality; it only decides which of several same-cost routes is returned.
   * The square grid uses it to prefer orthogonal steps over equal-cost diagonals,
   * killing the gratuitous "diagonal zag" an unbiased A* picks arbitrarily on a
   * straight run. Defaults to `0` (no preference). Keep per-step values small and
   * comparable in scale to a step (the grid uses `1` per diagonal) — the epsilon
   * weighting assumes the *summed* tie over a path stays well under one unit of
   * real cost, which holds for any sane map.
   */
  tieBreak?: (to: P, from: P) => number;
  /**
   * Whether the edge `from → to` is a **stopping edge**: entering `to` across it
   * is legal — `to` joins {@link reachable}'s map / can be an {@link aStar} goal,
   * exactly like any other in-budget cell — but `to` is never *expanded*: nothing
   * beyond it is reached BY WAY OF it (the search still may reach the same cell
   * some other, non-stopping way). Unlike an impassable step (`enterCost` ⇒
   * `Infinity`, which blocks entry entirely), a stopping edge blocks only
   * *continuing on* — enterable-but-terminal. This is generic graph-search
   * shape, not a rules concept: this module has no notion of engagement,
   * hostiles, or Size, only that some edges are one-way stops. Optional; absent
   * ⇒ no edge stops.
   *
   * **Flood-cost-order corner:** if a cell is reachable at its cheapest cost via
   * BOTH a stopping and a non-stopping edge, whichever edge relaxation the
   * search processes *first at that minimal cost* decides the recorded arrival
   * (and so its terminal-ness) — ties don't re-relax. Exotic geometries only;
   * pinned by a documented test in `movement.test.ts`, not silently "fixed" into
   * a two-state flood.
   */
  stopsAt?: (from: P, to: P) => boolean;
}

// How heavily a step's `tieBreak` penalty leans on the search priority. Tiny by
// design: a real cost increment is `>= 1` (every `enterCost >= 1`), so as long as
// the summed tie over a path stays well under `1 / TIE_EPS` it can only reorder
// genuinely-equal-cost expansions — never make the search prefer a costlier path.
const TIE_EPS = 1e-3;

/** Named arguments for {@link aStar} — the grid, endpoints, and {@link PathOptions}. */
export type AStarArgs<P> = {
  space: Space<P>;
  start: P;
  goal: P;
} & PathOptions<P>

/** Named arguments for {@link reachable} — the grid, origin, budget, and {@link PathOptions}. */
export type ReachableArgs<P> = {
  space: Space<P>;
  start: P;
  budget: number;
} & PathOptions<P>

/** A minimal binary min-heap keyed by `priority`. */
class MinHeap<T> {
  private readonly items: { value: T; priority: number }[] = [];

  get size(): number {
    return this.items.length;
  }

  push(value: T, priority: number): void {
    const items = this.items;
    items.push({ value, priority });
    let i = items.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (items[parent]!.priority <= items[i]!.priority) break;
      [items[parent], items[i]] = [items[i]!, items[parent]!];
      i = parent;
    }
  }

  pop(): T | undefined {
    const items = this.items;
    const top = items[0];
    if (top === undefined) return undefined;
    const last = items.pop()!;
    if (items.length > 0) {
      items[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1;
        const r = 2 * i + 2;
        let smallest = i;
        if (l < items.length && items[l]!.priority < items[smallest]!.priority) smallest = l;
        if (r < items.length && items[r]!.priority < items[smallest]!.priority) smallest = r;
        if (smallest === i) break;
        [items[smallest], items[i]] = [items[i]!, items[smallest]!];
        i = smallest;
      }
    }
    return top.value;
  }
}

/**
 * Shortest path from `start` to `goal` inclusive, or `null` if `goal` is
 * unreachable. Uses A* with the configured `enterCost` and `heuristic`. The
 * cost to enter `start` is never charged; `goal`'s `enterCost` must be finite
 * (you can't path *into* a blocked cell).
 *
 * **Termination requires a bounded passable region.** The grid is infinite, so
 * if `goal` is unreachable but the passable area around it is *unbounded*, the
 * search expands forever. Real maps are finite — make off-map cells impassable
 * (`enterCost` ⇒ `Infinity`) and the frontier is bounded. {@link reachable} has
 * no such caveat (a finite `budget` bounds it).
 */
export function aStar<P>(args: AStarArgs<P>): P[] | null {
  const { space, start, goal } = args;
  const enterCost = args.enterCost ?? (() => 1);
  const heuristic = args.heuristic ?? ((from: P, goal: P) => space.distance(from, goal));
  const tieBreak = args.tieBreak ?? (() => 0);
  const stopsAt = args.stopsAt ?? (() => false);

  const startKey = space.key(start);
  const goalKey = space.key(goal);
  if (startKey === goalKey) return [start];

  const frontier = new MinHeap<P>();
  frontier.push(start, 0);
  const cameFrom = new Map<string, P | null>([[startKey, null]]);
  const costSoFar = new Map<string, number>([[startKey, 0]]);
  // The cheapest-cost path's accrued tiebreak penalty per cell — the secondary key
  // that decides between equal-`cost` routes (lower = preferred). Never mixed into
  // `costSoFar`, so optimality is by true cost alone.
  const tieSoFar = new Map<string, number>([[startKey, 0]]);
  // Cells whose CURRENT best-known arrival is via a stopping edge — never expanded
  // (but may still be popped as the goal itself; see the check below). Tracks the
  // winning relaxation only: a later strictly-cheaper non-stopping arrival clears
  // the flag, exactly mirroring `reachable`'s flood-cost-order resolution.
  const terminal = new Set<string>();

  while (frontier.size > 0) {
    const current = frontier.pop()!;
    const currentKey = space.key(current);
    if (currentKey === goalKey) break;
    // Entered via a stopping edge: legal to have landed on, never a thoroughfare.
    if (terminal.has(currentKey)) continue;
    const baseCost = costSoFar.get(currentKey)!;
    const baseTie = tieSoFar.get(currentKey)!;

    for (const next of space.neighbors(current)) {
      const step = enterCost(next, current);
      if (!Number.isFinite(step)) continue;
      const newCost = baseCost + step;
      const newTie = baseTie + tieBreak(next, current);
      const nextKey = space.key(next);
      const prev = costSoFar.get(nextKey);
      // Relax on a strictly cheaper route, OR an equal-cost route with a lower
      // tiebreak (the orthogonal-over-diagonal preference). The epsilon-weighted
      // priority also makes the heap expand low-tie routes first, so the goal is
      // reached via the preferred path.
      if (prev === undefined || newCost < prev || (newCost === prev && newTie < tieSoFar.get(nextKey)!)) {
        costSoFar.set(nextKey, newCost);
        tieSoFar.set(nextKey, newTie);
        cameFrom.set(nextKey, current);
        if (stopsAt(current, next)) terminal.add(nextKey);
        else terminal.delete(nextKey);
        frontier.push(next, newCost + heuristic(next, goal) + TIE_EPS * newTie);
      }
    }
  }

  if (!cameFrom.has(goalKey)) return null;

  // Reconstruct, walking parent pointers back to start.
  const path: P[] = [];
  let step: P | null = goal;
  while (step !== null) {
    path.push(step);
    step = cameFrom.get(space.key(step)) ?? null;
  }
  path.reverse();
  return path;
}

/** A cell reachable within budget, with the cheapest cost to arrive there. */
export type Reachable<P> = {
  readonly cell: P;
  readonly cost: number;
}

/**
 * Every cell reachable from `start` for a total movement `budget`, keyed by
 * `space.key`, each with its cheapest arrival cost (Dijkstra flood fill).
 * `start` is always included at cost 0. Entering `start` is free; every other
 * cell is charged its `enterCost`. Cells whose cheapest arrival exceeds `budget`
 * are excluded.
 */
export function reachable<P>(args: ReachableArgs<P>): Map<string, Reachable<P>> {
  const { space, start, budget } = args;
  const enterCost = args.enterCost ?? (() => 1);
  const stopsAt = args.stopsAt ?? (() => false);
  const out = new Map<string, Reachable<P>>([[space.key(start), { cell: start, cost: 0 }]]);
  // Cells whose CURRENT best-known (cheapest) arrival is via a stopping edge —
  // included in `out` like any reachable cell, but never expanded further. See
  // `PathOptions.stopsAt` for the flood-cost-order resolution this tracks.
  const terminal = new Set<string>();

  const frontier = new MinHeap<P>();
  frontier.push(start, 0);

  while (frontier.size > 0) {
    const current = frontier.pop()!;
    const currentKey = space.key(current);
    // Entered via a stopping edge: joined the result, but not a thoroughfare.
    if (terminal.has(currentKey)) continue;
    const baseCost = out.get(currentKey)!.cost;

    for (const next of space.neighbors(current)) {
      const step = enterCost(next, current);
      if (!Number.isFinite(step)) continue;
      const newCost = baseCost + step;
      if (newCost > budget) continue;
      const nextKey = space.key(next);
      const existing = out.get(nextKey);
      if (existing === undefined || newCost < existing.cost) {
        out.set(nextKey, { cell: next, cost: newCost });
        if (stopsAt(current, next)) terminal.add(nextKey);
        else terminal.delete(nextKey);
        frontier.push(next, newCost);
      }
    }
  }

  return out;
}
